import React from 'react';
import { AppConfig } from 'Generated/schema';

export type AppConfigContextType = [AppConfig, React.Dispatch<React.SetStateAction<AppConfig>>];

export const AppConfigContext = React.createContext(null);

const AppConfigProvider = ({
  initialAppConfig,
  children,
}: {
  initialAppConfig?: AppConfig;
  children: React.ReactNode;
}) => {
  const [appConfig, setAppConfig] = React.useState<AppConfig>(initialAppConfig);
  const contextValue = React.useMemo(() => [appConfig, setAppConfig], [appConfig, setAppConfig]);

  return <AppConfigContext.Provider value={contextValue}>{children}</AppConfigContext.Provider>;
};

const useUpdateAppConfig = () => {
  const context = React.useContext<AppConfigContextType>(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfigContext must be used within a AppConfigProvider');
  }

  return context[1];
};

const useAppConfig = () => {
  const context = React.useContext<AppConfigContextType>(AppConfigContext);
  if (context?.[0] === undefined) {
    throw new Error(
      'useAppConfigContext must be used within a AppConfigProvider or the app config has not been initialized.',
    );
  }
  const appConfig = React.useMemo(() => context[0], [context]);

  return appConfig;
};

const useAppConfigSafe = (): AppConfig | undefined => {
  const context = React.useContext<AppConfigContextType>(AppConfigContext);
  return context?.[0];
};

const MemoAppConfigProvider = React.memo(AppConfigProvider);

export {
  MemoAppConfigProvider as AppConfigProvider,
  useAppConfig,
  useAppConfigSafe,
  useUpdateAppConfig,
};
